import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { login } from '../redux/slice/auth.slice';
import { Eye, EyeOff, Lock, Mail, ArrowRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const Login = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { loading, isAuthenticated } = useSelector((state) => state.auth);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (isAuthenticated) {
            navigate('/projects');
        }
    }, [isAuthenticated, navigate]);

    const formik = useFormik({
        initialValues: {
            email: '',
            password: ''
        },
        validationSchema: Yup.object({
            email: Yup.string().email('Invalid email address').required('Email is required'),
            password: Yup.string().required('Password is required')
        }),
        onSubmit: async (values) => {
            const resultAction = await dispatch(login(values));
            if (login.fulfilled.match(resultAction)) {
                toast.success("Login successful");
                navigate('/projects');
            } else {
                toast.error(resultAction.payload?.message || "Login failed");
            }
        }
    });

    return (
        <div className="min-h-screen bg-surface flex items-center justify-center p-4 relative overflow-hidden">
            {/* Background Decor */}
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-secondary/5 blur-[100px] rounded-full pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-900/5 blur-[100px] rounded-full pointer-events-none" />

            <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden relative z-10 animate-fade-in">
                <div className="p-8 sm:p-10">
                    <div className="text-center mb-10">
                        <h1 className="text-3xl font-serif font-bold text-primary mb-2">Admin Panel</h1>
                        <p className="text-slate-500 text-sm">Sign in to manage your dashboard</p>
                    </div>

                    <form onSubmit={formik.handleSubmit} className="space-y-6">
                        <div>
                            <label className="block text-sm font-semibold text-primary mb-2">Email Address</label>
                            <div className="relative group">
                                <Mail className={`absolute top-3.5 left-4 w-5 h-5 transition-colors ${formik.touched.email && formik.errors.email ? 'text-red-500' : 'text-slate-400 group-focus-within:text-secondary'}`} />
                                <input
                                    type="email"
                                    name="email"
                                    value={formik.values.email}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:ring-4 transition-all outline-none bg-slate-50/50 text-gray-700 font-medium placeholder:font-normal ${formik.touched.email && formik.errors.email ? 'border-red-500 focus:ring-red-100 focus:border-red-500' : 'border-gray-200 focus:ring-secondary/10 focus:border-secondary'}`}
                                    placeholder="Enter your email"
                                />
                            </div>
                            {formik.touched.email && formik.errors.email && (
                                <p className="text-red-500 text-xs mt-1 ml-1">{formik.errors.email}</p>
                            )}
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-primary mb-2">Password</label>
                            <div className="relative group">
                                <Lock className={`absolute top-3.5 left-4 w-5 h-5 transition-colors ${formik.touched.password && formik.errors.password ? 'text-red-500' : 'text-slate-400 group-focus-within:text-secondary'}`} />
                                <input
                                    type={showPassword ? "text" : "password"}
                                    name="password"
                                    value={formik.values.password}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    className={`w-full pl-12 pr-12 py-3 border rounded-xl focus:ring-4 transition-all outline-none bg-slate-50/50 text-gray-700 font-medium placeholder:font-normal ${formik.touched.password && formik.errors.password ? 'border-red-500 focus:ring-red-100 focus:border-red-500' : 'border-gray-200 focus:ring-secondary/10 focus:border-secondary'}`}
                                    placeholder="Enter your password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute top-3.5 right-4 text-slate-400 hover:text-secondary transition-colors focus:outline-none"
                                >
                                    {showPassword ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
                                </button>
                            </div>
                            {formik.touched.password && formik.errors.password && (
                                <p className="text-red-500 text-xs mt-1 ml-1">{formik.errors.password}</p>
                            )}
                        </div>

                        <div className="flex items-center justify-end">
                            <button type="button" onClick={() => navigate('/forgot-password')} className="text-sm font-medium text-secondary hover:text-primary transition-colors">
                                Forgot Password?
                            </button>
                        </div>

                        <button
                            type="submit"
                            disabled={loading}
                            className="w-full py-3.5 bg-primary text-white font-bold rounded-xl shadow-lg shadow-blue-900/10 hover:bg-tertiary transition-all flex items-center justify-center gap-2 group disabled:opacity-70 disabled:cursor-not-allowed"
                        >
                            {loading ? (
                                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                            ) : (
                                <>
                                    Sign In <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </>
                            )}
                        </button>
                    </form>
                </div>
                <div className="bg-slate-50 p-4 text-center border-t border-gray-100">
                    <p className="text-xs text-slate-400">© 2026 Admin Dashboard. All rights reserved.</p>
                </div>
            </div>
        </div>
    );
};

export default Login;
